import React, { useState, useEffect, useRef } from 'react';
import { Popup, Position, Button } from '../BootstrapControls';
import Axios from '../../api/axios';
import { getByDownloadRef, getMediaKindFromPath, isVideoPath } from '../../utils/mediaCache.js';

export default function ResimIzlePanel({ resimList, visible, onClose, initialIndex = 0 }) {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);

    useEffect(() => {
        if (visible) setCurrentIndex(initialIndex);
    }, [visible, initialIndex]);

    const goToPrev = () => {
        setCurrentIndex(i => (i - 1 + resimList.length) % resimList.length);
    };

    const goToNext = () => {
        setCurrentIndex(i => (i + 1) % resimList.length);
    };

    const downloadCurrentImage = async () => {
        if (!currentImage || !currentImage.CR_REF) return;
        try {
            const fileName = currentImage.CR_RESIM ? currentImage.CR_RESIM.split(/[\\\/]/).pop() : `resim_${currentImage.CR_REF}`;
            const response = await Axios.get(
                `${process.env.REACT_APP_BACKEND_SERVER}fileops/download?CR_REF=${currentImage.CR_REF}`,
                { responseType: 'blob' }
            );
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            link.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.log('Download error:', err);
        }
    };

    const handleKeyDown = (e) => {
        if (!visible) return;
        if (e.key === 'Escape') onClose();
        else if (e.key === 'ArrowLeft') goToPrev();
        else if (e.key === 'ArrowRight') goToNext();
    };

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, resimList, currentIndex]);

    // Media state must be declared before any early return (Hooks rule)
    const [media, setMedia] = useState({ url: '', kind: 'image' });
    const lastRef = useRef(null);

    // Compute current image safely; effect below guards when invisible or empty
    const hasList = Array.isArray(resimList) && resimList.length > 0;
    const currentImage = hasList ? resimList[currentIndex] : null;

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!visible || !currentImage || !currentImage.CR_REF) { setMedia({ url: '', kind: 'image' }); return; }
            const pathHint = currentImage.CR_RESIM || '';
            const kind = getMediaKindFromPath(pathHint);
            if (isVideoPath(pathHint) || kind === 'video') {
                const streamUrl = `${process.env.REACT_APP_BACKEND_SERVER}fileops/stream?path=${encodeURIComponent(pathHint)}`;
                if (!cancelled) setMedia({ url: streamUrl, kind: 'video' });
                return;
            }
            if (kind === 'pdf') {
                const pdfUrl = `${process.env.REACT_APP_BACKEND_SERVER}fileops/download?CR_REF=${currentImage.CR_REF}`;
                if (!cancelled) setMedia({ url: pdfUrl, kind: 'pdf' });
                return;
            }
            if (kind === 'office') {
                const name = pathHint ? pathHint.split(/[\\/]/).pop() : `dosya_${currentImage.CR_REF}`;
                if (!cancelled) setMedia({ url: '', kind: 'office', name });
                return;
            }
            try {
                const m = await getByDownloadRef(process.env.REACT_APP_BACKEND_SERVER, currentImage.CR_REF, pathHint);
                if (!cancelled) setMedia(m);
                lastRef.current = currentImage.CR_REF;
            } catch (e) {
                if (!cancelled) setMedia({ url: '', kind: 'image' });
            }
        })();
        return () => { cancelled = true; };
    }, [visible, currentImage]);

    if (!visible || !hasList) return null;

    return (
        <Popup
            visible={visible}
            onHiding={onClose}
            showCloseButton={true}
            dragEnabled={false}
            width="95vw"
            height="95vh"
            shading={true}
            shadingColor="rgba(0,0,0,0.85)"
            closeOnOutsideClick={true}
        >
            <Position my="center" at="center" of={window} />
            <div style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#000',
                color: '#fff'
            }}>
                {/* Ana resim */}
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', overflow: 'hidden' }}>
                    {media.kind === 'video' ? (
                        <video
                            src={media.url}
                            controls
                            autoPlay
                            style={{ maxWidth: '100%', maxHeight: '100%', background: 'black' }}
                        />
                    ) : media.kind === 'pdf' ? (
                        <iframe
                            src={media.url}
                            title="PDF Preview"
                            style={{ width: '100%', height: '100%', border: 'none', background: 'white' }}
                            sandbox="allow-scripts allow-same-origin"
                        />
                    ) : media.kind === 'office' ? (
                        <div style={{ textAlign: 'center', padding: 24 }}>
                            <div style={{ fontSize: 64, marginBottom: 12 }} aria-hidden>📑</div>
                            <div style={{ fontSize: 16, marginBottom: 8, wordBreak: 'break-all' }}>{media.name || 'Ofis belgesi'}</div>
                            <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 16 }}>Önizleme desteklenmiyor — dosyayı indirebilirsiniz</div>
                            <Button
                                icon="download"
                                text="İndir"
                                type="default"
                                stylingMode="contained"
                                onClick={downloadCurrentImage}
                            />
                        </div>
                    ) : (
                        <img
                            src={media.url}
                            alt={`Medya ${currentIndex + 1}`}
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                    )}
                </div>

                {/* Sol ok */}
                {resimList.length > 1 && (
                    <div
                        onClick={goToPrev}
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: 16,
                            transform: 'translateY(-50%)',
                            cursor: 'pointer',
                            fontSize: 48,
                            userSelect: 'none',
                            color: '#fff',
                            background: 'rgba(0,0,0,0.5)',
                            borderRadius: '50%',
                            width: 60,
                            height: 60,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background 0.2s',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.7)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.5)'}
                    >
                        ‹
                    </div>
                )}

                {/* Sağ ok */}
                {resimList.length > 1 && (
                    <div
                        onClick={goToNext}
                        style={{
                            position: 'absolute',
                            top: '50%',
                            right: 16,
                            transform: 'translateY(-50%)',
                            cursor: 'pointer',
                            fontSize: 48,
                            userSelect: 'none',
                            color: '#fff',
                            background: 'rgba(0,0,0,0.5)',
                            borderRadius: '50%',
                            width: 60,
                            height: 60,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background 0.2s',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.7)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.5)'}
                    >
                        ›
                    </div>
                )}

                {/* Resim bilgisi (üst) */}
                <div style={{
                    position: 'absolute',
                    top: 16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    gap: 12,
                    alignItems: 'center',
                    background: 'rgba(0,0,0,0.7)',
                    padding: '8px 16px',
                    borderRadius: 4,
                    maxWidth: '80%'
                }}>
                    {currentImage?.CR_ACK && (
                        <div style={{ textAlign: 'center', color: '#fff' }}>
                            {currentImage.CR_ACK}
                        </div>
                    )}
                    <Button
                        icon="download"
                        text="İndir"
                        type="default"
                        stylingMode="contained"
                        onClick={downloadCurrentImage}
                    />
                </div>

                {/* Alt sayfa numaraları */}
                {resimList.length > 1 && (
                    <div style={{
                        position: 'absolute',
                        bottom: 16,
                        display: 'flex',
                        gap: 8,
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        padding: '8px 16px',
                        background: 'rgba(0,0,0,0.6)',
                        borderRadius: 4,
                        maxWidth: '90%',
                        overflowX: 'auto'
                    }}>
                        {resimList.map((r, idx) => (
                            <div
                                key={r.CR_REF || idx}
                                onClick={() => setCurrentIndex(idx)}
                                style={{
                                    cursor: 'pointer',
                                    padding: '6px 10px',
                                    borderRadius: 4,
                                    background: idx === currentIndex ? '#1976d2' : 'rgba(255,255,255,0.2)',
                                    color: '#fff',
                                    fontWeight: idx === currentIndex ? 'bold' : 'normal',
                                    userSelect: 'none',
                                    minWidth: 32,
                                    textAlign: 'center',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                    if (idx !== currentIndex) {
                                        e.currentTarget.style.background = 'rgba(255,255,255,0.4)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (idx !== currentIndex) {
                                        e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                                    }
                                }}
                            >
                                {idx + 1}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Popup>
    );
}
